import { EGRESS_AUDIENCES, type EgressAction, type EgressPolicy } from "@esi/shared";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { SettingSegmented } from "./controls.tsx";
import { type EgressAudience, egressAudienceName } from "./egress.ts";

const ACTIONS = [
  { value: "allow", label: "settingsControls.segments.allowed" },
  { value: "confirm", label: "settingsControls.segments.asksFirst" },
  { value: "block", label: "settingsControls.segments.blocked" },
] as const satisfies readonly { value: EgressAction; label: string }[];

/**
 * `tools.egress` per readers audience (openspec change settings-typed-controls, D10): one line
 * per audience (Project audience, Owning entity, Named readers), each with its help in readers'
 * terms and an "Allowed | Asks first | Blocked" segmented control named by the audience. The row
 * label names the group. A change hands back the whole policy with that audience changed, so the
 * row saves it in one write; `busy` holds every line while it runs.
 */
export function SettingEgress(props: {
  labelledBy: string;
  describedBy?: string;
  busy?: boolean;
  disabled?: boolean;
  value: EgressPolicy;
  /** Per audience, the actions shown but not offered (looser than a project inherits, D6). */
  disabledActions?: Partial<Record<EgressAudience, readonly EgressAction[]>>;
  onChange: (next: EgressPolicy) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    // biome-ignore lint/a11y/useSemanticElements: three radio groups under one label, no form.
    <div
      role="group"
      aria-labelledby={props.labelledBy}
      aria-describedby={props.describedBy}
      className="flex w-full flex-col divide-y divide-line"
    >
      {EGRESS_AUDIENCES.map((audience) => {
        const nameId = `${id}-${audience}`;
        const helpId = `${id}-${audience}-help`;
        return (
          <div
            key={audience}
            data-egress-audience={audience}
            className="flex flex-col gap-2 py-2.5 first:pt-0 last:pb-0 @md:flex-row @md:items-center @md:justify-between @md:gap-4"
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span id={nameId} className="text-sm text-fg">
                {egressAudienceName(audience)}
              </span>
              <span id={helpId} className="text-xs text-fg-muted">
                {t(`settingsControls.egress.help.${audience}`)}
              </span>
            </span>
            <span className="shrink-0">
              <SettingSegmented
                labelledBy={nameId}
                describedBy={[helpId, props.describedBy].filter(Boolean).join(" ")}
                busy={props.busy}
                disabled={props.disabled}
                value={props.value[audience]}
                options={ACTIONS.map((a) => ({
                  value: a.value,
                  label: t(a.label),
                  disabled: props.disabledActions?.[audience]?.includes(a.value),
                }))}
                onChange={(next) =>
                  props.onChange({ ...props.value, [audience]: next as EgressAction })
                }
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}
